import { JSX, useContext, useEffect, useMemo, useState } from "react";

import { LoadingSpinner, PageSection, SearchBox } from "@app/shared";
import { QESCheckbox } from "@app/shared/qes-standards";
import { VolumeReportsContext } from "../import/VolumeReportsContext";
import { VolumeReportsStateAccordionWithCheckboxes } from "../import/VolumeReportsStateAccordionWithCheckboxes";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { WizardStepProps } from "../import/interfaces";

const VolumeReportsSelectServiceArea = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);

  const [serviceAreaSearchString, setServiceAreaSearchString] = useState("");

  useEffect(() => {
    wizardContext.updateRegionVisibility(serviceAreaSearchString);
  }, [serviceAreaSearchString]);

  useEffect(() => {
    props.pageIsValid(wizardContext.serviceAreas.length > 0);
  }, [wizardContext.serviceAreas]);

  const renderedRegions = useMemo(() => {
    // we append the search string to react's key prop to force a component remount on change, since we can't programatically tell it to open or close
    return wizardContext.serviceAreas.filter((state) => state.isVisible).map((state) =>
      <VolumeReportsStateAccordionWithCheckboxes key={state.displayName + serviceAreaSearchString} state={state} />
    );
  }, [wizardContext.serviceAreas]);

  const allStatesChecked = useMemo(() => {
    return wizardContext.serviceAreas.every((state) => state.checkboxState === CheckboxStateEnum.Checked);
  }, [wizardContext.serviceAreas]);

  // if there's a filter and all visible counties are checked, clicking again should uncheck all visible counties
  const allVisibleCountiesChecked = useMemo(() => {
    if (!serviceAreaSearchString) {
      return false;
    }
    return wizardContext.serviceAreas
      .filter((state) => state.isVisible)
      .flatMap((state) => state.childServiceAreas)
      .filter((child) => child.isVisible)
      .every((child) => child.isChecked);
  }, [wizardContext.serviceAreas, serviceAreaSearchString]);

  const someStatesChecked = useMemo(() => {
    return wizardContext.serviceAreas.some((state) => state.checkboxState === CheckboxStateEnum.Checked || state.checkboxState === CheckboxStateEnum.Indeterminate);
  }, [wizardContext.serviceAreas]);

  return (
    <PageSection>
      <div className="container mt-3">
        {wizardContext.reportType !== "stateaggregate" && 
          (<SearchBox
            escapeSearchValue={true}
            fieldsetClass="row"
            inputLabel="Search Counties"
            testId="volume-reports-search-service-areas"
            onSubmit={(value: string) => setServiceAreaSearchString(value)}
          />)
        }
      </div>
      <div className="container">
        {wizardContext.loading ? <LoadingSpinner /> : (
          <div className="row">
            <div className="w-100">
              <div className="border">
                <div className="vr__check-all-container">
                  <QESCheckbox
                    label="States"
                    checked={allStatesChecked}
                    indeterminate={someStatesChecked && !allStatesChecked}
                    onChange={(e) => wizardContext.updateSelectedServiceArea(undefined, undefined, allVisibleCountiesChecked ? false : e.target.checked)}
                    testId="volume-reports__all-states-checkbox"
                    containerClassName="vr__check-all"
                  />
                </div>
                {renderedRegions}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageSection>
  );
};

export default VolumeReportsSelectServiceArea;
